/* One list of hobbies, of one kind.
 *
 * Rendered twice — for the things you keep up and for people — because the two
 * behave identically to the planner and read completely differently to the
 * person. Keeping them as one component with a category rather than two copies
 * means the sentence about how a claim works is written once.
 *
 * The number asked for is **times a week**, not hours. That is the change that
 * matters: "three times" is what somebody means about the gym, and turning it
 * into 180 minutes loses the part the solver most needs — that it wants three
 * separate sessions rather than one long one.
 */

import { useState } from 'react';
import type { FormEvent } from 'react';
import { useMutation } from '@tanstack/react-query';
import type { Hobby, HobbyCategory, HobbySuggestion } from '@shared/types';
import { post, patch, del, errorMessage } from '../../api';
import { useToast } from '../../ui/toast';
import { hours, human } from '../../ui/time';
import { ConfirmDialog } from '../../ui/schedule';
import Section from '../../ui/Section';
import Icon from '../../ui/Icon';

/* A session length that is worth placing. Below about half an hour the solver
   is scheduling an interruption rather than an activity. */
const SESSION_CHOICES = [30, 45, 60, 90, 120];

export default function HobbyList({ title, blurb, category, hobbies, suggested, onChanged }: {
  title: string;
  blurb: string;
  category: HobbyCategory;
  hobbies: Hobby[];
  suggested: HobbySuggestion[];
  onChanged: () => void;
}) {
  const toast = useToast();
  const [adding, setAdding] = useState(false);
  const [dropping, setDropping] = useState<Hobby | null>(null);

  const add = useMutation({
    mutationFn: async (body: Record<string, unknown>) => {
      const r = await post('/api/hobbies', { ...body, category });
      if (!r.ok) throw new Error(errorMessage(r, 'Could not add that.'));
      return body.name as string;
    },
    onSuccess: name => {
      onChanged();
      setAdding(false);
      toast.say(`${name} has a claim on your week now — Amon holds the time before work gets it.`,
        'good');
    },
    onError: (e: Error) => toast.say(e.message, 'bad')
  });

  const change = useMutation({
    mutationFn: async ({ h, body }: { h: Hobby; body: Record<string, unknown> }) => {
      const r = await patch(`/api/hobbies/${h.typeId}`, body);
      if (!r.ok) throw new Error(errorMessage(r, 'Could not change that.'));
      return r;
    },
    onSuccess: onChanged,
    onError: (e: Error) => toast.say(e.message, 'bad')
  });

  const drop = useMutation({
    mutationFn: async (h: Hobby) => {
      const r = await del(`/api/hobbies/${h.typeId}`);
      if (!r.ok) throw new Error(errorMessage(r, 'Could not remove that.'));
      return h;
    },
    onSuccess: h => {
      onChanged();
      toast.say(`Stopped holding time for ${h.name}. The hours you already spent still count.`);
    },
    onError: (e: Error) => toast.say(e.message, 'bad')
  });

  const unused = suggested.filter(s =>
    !hobbies.some(h => h.name.toLowerCase() === s.name.toLowerCase()));

  return (
    <Section id={`${category}-title`} icon={category === 'social' ? 'people' : 'repeat'}
             className="tone-green"
             title={title}
             meta={hobbies.length
               ? `${hobbies.length} claimed · ${hours(hobbies.reduce((a, h) => a + h.minutesPerWeek, 0))} a week`
               : category === 'social' ? 'friends and family, held like anything else' : 'weekly, by sessions'}
             info={blurb}
             action={!adding && (
               <button className="btn btn-ghost btn-sm" onClick={() => setAdding(true)}>
                 <Icon name="plus" size={16} />Add
               </button>
             )}>

      {adding && (
        <HobbyForm
          category={category}
          onCancel={() => setAdding(false)}
          onSave={body => add.mutate(body)}
          saving={add.isPending} />
      )}

      {hobbies.length === 0 && !adding && (
        <p className="muted small">
          {category === 'social'
            ? 'Nothing claimed, so the first busy week takes it.'
            : 'Nothing claimed yet, which means every hour you are awake is offered to work first.'}
        </p>
      )}

      {unused.length > 0 && !adding && (
        <div className="hobby-suggest">
          <span className="muted small" id={`${category}-suggest`}>One tap:</span>
          <div className="inline wrap" role="group" aria-labelledby={`${category}-suggest`}>
            {unused.map(s => (
              <button key={s.name} className="btn btn-ghost btn-sm"
                      disabled={add.isPending}
                      onClick={() => add.mutate({
                        name: s.name, color: s.color,
                        minutesPerWeek: s.minutesPerWeek,
                        sessionsPerWeek: Math.max(1, Math.round(s.minutesPerWeek / 60)),
                        kind: 'floor'
                      })}>
                <Icon name="plus" size={14} />{s.name}
              </button>
            ))}
          </div>
        </div>
      )}

      {hobbies.map(h => {
        const sessions = h.sessionsPerWeek ?? Math.max(1, Math.round(h.minutesPerWeek / 60));
        const each = h.sessionMin ?? Math.round(h.minutesPerWeek / sessions);
        const doneSessions = h.doneMin != null && each ? Math.floor(h.doneMin / each) : null;

        return (
          <article key={h.typeId} className={`hobby hobby-${h.color}`}>
            <div className="hobby-head">
              <Ring value={h.doneMin ?? 0} of={Math.max(1, h.minutesPerWeek)} met={!!h.met} />
              <div className="hobby-titles">
                <strong>{h.name}</strong>
                <span className="muted small">{sessions}× {human(each)} a week</span>
              </div>
              {h.met && <span className="chip chip-done"><Icon name="check" size={12} />done this week</span>}
              <button className="btn btn-ghost btn-icon btn-bad hobby-drop" onClick={() => setDropping(h)}
                      aria-label="Stop claiming" title={`Stop claiming time for ${h.name}`}>
                <Icon name="trash" size={17} />
              </button>
            </div>

            {h.doneMin === null ? (
              <p className="muted small">{h.note}</p>
            ) : (
              <>
                <div className="meter" role="img"
                     aria-label={`${hours(h.doneMin)} of ${hours(h.minutesPerWeek)} this week`}>
                  <div className={`meter-fill${h.met ? ' is-met' : ''}`}
                       style={{ width: `${Math.min(100, Math.round((h.doneMin / Math.max(1, h.minutesPerWeek)) * 100))}%` }} />
                </div>
                <p className="muted small">
                  {doneSessions !== null
                    ? `${doneSessions} of ${sessions} this week`
                    : `${hours(h.doneMin)} of ${hours(h.minutesPerWeek)}`}
                  {' — '}{h.note}
                </p>
              </>
            )}

            <div className="inline wrap">
              <label className="db-field">
                <span>Times a week</span>
                <select value={sessions}
                        aria-label={`Times a week for ${h.name}`}
                        onChange={e => change.mutate({ h, body: {
                          sessionsPerWeek: Number(e.target.value),
                          minutesPerWeek: Number(e.target.value) * each
                        } })}>
                  {[1, 2, 3, 4, 5, 6, 7].map(n => <option key={n} value={n}>{n}</option>)}
                </select>
              </label>
              <label className="db-field">
                <span>Each time</span>
                <select value={each}
                        aria-label={`How long each time for ${h.name}`}
                        onChange={e => change.mutate({ h, body: {
                          minutesPerWeek: sessions * Number(e.target.value)
                        } })}>
                  {/* The saved length, even when it is not one of the usual
                      ones: a three-hour session shown as "30m" is the select
                      quietly misreporting what is stored. */}
                  {[...new Set([...SESSION_CHOICES, each])].sort((a, b) => a - b)
                    .map(m => <option key={m} value={m}>{human(m)}</option>)}
                </select>
              </label>
              <label className="db-field">
                <span>Claim</span>
                <select value={h.kind ?? 'floor'}
                        aria-label={`Kind of claim for ${h.name}`}
                        onChange={e => change.mutate({ h, body: { kind: e.target.value } })}>
                  <option value="floor">Held back for it</option>
                  <option value="target">Measured, not enforced</option>
                </select>
              </label>
            </div>
          </article>
        );
      })}

      {dropping && (
        <ConfirmDialog
          title={`Stop claiming time for ${dropping.name}?`}
          description="The category and the hours you already spent stay — this only stops Amon
                       holding time back for it."
          confirmLabel="Stop claiming"
          onClose={() => setDropping(null)}
          onConfirm={() => drop.mutateAsync(dropping)} />
      )}
    </Section>
  );
}

/* Progress as a ring rather than a bar: it sits beside the name like an
   avatar, so a list of hobbies scans as "how far along is each" without a
   second row per hobby. The words are in the line below it for anyone who
   cannot see the arc. */
function Ring({ value, of, met }: { value: number; of: number; met: boolean }) {
  const pct = Math.min(1, value / of);
  const r = 15, c = 2 * Math.PI * r;
  return (
    <svg className={`ring${met ? ' is-met' : ''}`} width="40" height="40" viewBox="0 0 40 40" aria-hidden="true">
      <circle cx="20" cy="20" r={r} className="ring-track" />
      <circle cx="20" cy="20" r={r} className="ring-fill"
              strokeDasharray={c} strokeDashoffset={c * (1 - pct)} />
      <text x="20" y="24" textAnchor="middle" className="ring-text">{Math.round(pct * 100)}</text>
    </svg>
  );
}

function HobbyForm({ category, onCancel, onSave, saving }: {
  category: HobbyCategory;
  onCancel: () => void;
  onSave: (body: Record<string, unknown>) => void;
  saving: boolean;
}) {
  const [name, setName] = useState('');
  const [sessions, setSessions] = useState('2');
  const [each, setEach] = useState('60');
  const [error, setError] = useState<string | null>(null);

  const times = Math.max(1, Number(sessions) || 2);
  const minutes = Math.max(15, Number(each) || 60);

  return (
    <form className="when-form" onSubmit={(e: FormEvent) => {
      e.preventDefault();
      if (!name.trim()) { setError(category === 'social' ? 'Who?' : 'What is it?'); return; }
      setError(null);
      onSave({
        name: name.trim(),
        sessionsPerWeek: times,
        minutesPerWeek: times * minutes,
        kind: 'floor'
      });
    }}>
      <div className="field field-wide">
        <label htmlFor={`hobby-name-${category}`}>
          {category === 'social' ? 'Who, or what kind of time?' : 'What is it?'}
        </label>
        <input id={`hobby-name-${category}`} value={name} maxLength={40} autoFocus
               placeholder={category === 'social' ? 'Friends' : 'The gym'}
               onChange={e => setName(e.target.value)} />
      </div>

      <div className="field">
        <label htmlFor={`hobby-times-${category}`}>Times a week</label>
        <select id={`hobby-times-${category}`} value={sessions}
                onChange={e => setSessions(e.target.value)}>
          {[1, 2, 3, 4, 5, 6, 7].map(n => <option key={n} value={n}>{n}</option>)}
        </select>
      </div>

      <div className="field">
        <label htmlFor={`hobby-each-${category}`}>Each time</label>
        <select id={`hobby-each-${category}`} value={each}
                onChange={e => setEach(e.target.value)}>
          {SESSION_CHOICES.map(m => <option key={m} value={m}>{human(m)}</option>)}
        </select>
      </div>

      <p className="muted small field-wide">
        {times}× {human(minutes)} a week — {hours(times * minutes)} in total, held back
        before your week is offered to work.
      </p>

      {error && <p className="auth-err field-wide" role="alert">{error}</p>}

      <div className="inline field-wide">
        <button className="btn btn-primary btn-sm" type="submit" disabled={saving}>
          {saving ? 'Saving…' : 'Claim the time'}
        </button>
        <button className="btn btn-ghost btn-sm" type="button" onClick={onCancel}>Cancel</button>
      </div>
    </form>
  );
}
